import { useCallback, useState } from 'react'
import { advancedGet, advancedPost } from '../../api/client'
import { useAdvancedResource } from '../../api/useAdvancedResource'
import { useRefreshWhileVisible } from '../../api/useRefreshWhileVisible'
import { CareTeamThreadSummary } from './types'

const threadsPath = (uid: string) => `/patients/${encodeURIComponent(uid)}/care-team-chat/threads`

const POLL_MS = 30_000

export type CareTeamThreadsState = {
  threads: CareTeamThreadSummary[]
  groupUnreadCount: number
  loading: boolean
  // Re-reads in place, keeping the current list on screen until the new one lands.
  refresh: () => void
  startThread: (otherProviderUid: string) => Promise<CareTeamThreadSummary>
  // Lists a thread the viewer has just written in, ahead of the service listing it.
  markWrittenIn: (thread: CareTeamThreadSummary) => void
}

// The service lists only threads with a message (ADR-139); `enabled` holds the first load until the panel opens.
export const useCareTeamThreads = (uid: string, enabled = true): CareTeamThreadsState => {
  const resource = useAdvancedResource<{ threads: CareTeamThreadSummary[]; groupUnreadCount: number }>(
    (signal) => advancedGet(threadsPath(uid), signal),
    [uid],
    !enabled
  )
  const { retry } = resource
  const poll = useCallback(() => {
    if (enabled) {
      retry()
    }
  }, [enabled, retry])
  useRefreshWhileVisible(poll, POLL_MS)
  // Tagged so a thread written in for one patient never shows under the next.
  const [writtenIn, setWrittenIn] = useState<{ uid: string; threads: CareTeamThreadSummary[] }>()

  const startThread = useCallback(
    async (otherProviderUid: string) => {
      const { thread } = await advancedPost<{ thread: CareTeamThreadSummary }>(threadsPath(uid), {
        participantUid: otherProviderUid,
      })

      return thread
    },
    [uid]
  )

  const markWrittenIn = useCallback(
    (thread: CareTeamThreadSummary) =>
      setWrittenIn((current) => {
        const existing = current?.uid === uid ? current.threads : []

        return existing.some((known) => known.threadId === thread.threadId)
          ? current
          : { uid, threads: [...existing, thread] }
      }),
    [uid]
  )

  const loaded = resource.status === 'ready' ? resource.data.threads : []
  const groupUnreadCount = resource.status === 'ready' ? resource.data.groupUnreadCount : 0
  const threads =
    writtenIn?.uid === uid
      ? [
          ...loaded,
          ...writtenIn.threads.filter((thread) => !loaded.some((known) => known.threadId === thread.threadId)),
        ]
      : loaded

  return {
    threads,
    groupUnreadCount,
    loading: resource.status === 'loading',
    refresh: retry,
    startThread,
    markWrittenIn,
  }
}
